<script setup lang="ts">
import type { ApproveEmit, LineApproveProps } from '/#/components/base/approve'
import type { FlexJustifyContent } from '/#/components/base/flex/flex'
import type { ComputedRef } from 'vue'

import Button from '/@/components/base/button/Button.vue'

const props = withDefaults(defineProps<LineApproveProps>(), {
  title: undefined,
  transition: 'fade-fast',
  removeItemId: undefined,
  align: 'normal',
})

const emit = defineEmits<ApproveEmit>()

const { t } = useI18n()

const contentAlignment: ComputedRef<FlexJustifyContent> = computed(() => {
  if (props.align) return props.align
  return props.title ? 'flex-start' : 'center'
})

/**
 * @name approveButtonProps
 * @description Approve button props
 */
const approveButtonProps: ComputedRef<Recordable> = computed(() => {
  if (props.textButton) return { text: t('action.approve'), color: 'primary' }
  return {
    icon: {
      name: 'checkmark',
      size: 'lg',
    },
    variant: 'link',
    color: 'success',
    circle: true,
    size: 'xs',
  }
})

/**
 * @name rejectButtonProps
 * @description Reject button props
 */
const rejectButtonProps: ComputedRef<Recordable> = computed(() => {
  if (props.textButton)
    return { text: t('action.reject'), color: 'slate', variant: 'outlined' }
  return {
    icon: {
      name: 'close',
      size: 'lg',
    },
    variant: 'link',
    color: 'danger',
    circle: true,
    size: 'xs',
  }
})

// They decide what kind of tasks these function buttons do
const ApproveButton = () => h(Button, approveButtonProps.value)

const RejectButton = () => h(Button, rejectButtonProps.value)
</script>

<template>
  <Transition :name="transition" mode="out-in">
    <template v-if="removeItemId === itemId">
      <Flex
        flex-direction="row"
        :justify-content="contentAlignment"
        align-items="center"
        column-gap="0.7em"
        class="approve-remove-wrapper"
        :class="[inlineMode && 'inline-mode']"
      >
        <FlexItem v-if="title">
          <div class="remove-title">
            <span v-if="!loading">{{ title }}</span>
            <span v-else>{{ t('line-approve.waiting-process') }}</span>
          </div>
        </FlexItem>

        <FlexItem>
          <Flex column-gap="0.5rem">
            <FlexItem
              v-if="!loading"
             
              @click.prevent.stop="emit('reject')"
            >
              <RejectButton />
            </FlexItem>

            <FlexItem
              v-if="!loading"
             
              @click.prevent.stop="emit('approve')"
            >
              <ApproveButton />
            </FlexItem>

            <FlexItem v-else>
              <div class="remove-spinner">
                <Spinner class="mr-1.5" />
              </div>
            </FlexItem>
          </Flex>
        </FlexItem>
      </Flex>
    </template>

    <template v-else>
      <slot />
    </template>
  </Transition>
</template>

<style lang="scss" scoped>
.approve-remove-wrapper {
  height: 100%;
  width: 100%;
  padding: 0.5em 1em;
  min-width: 175px;
  background-color: map-get($background-colors, 'muted');

  &.inline-mode {
    @apply bg-transparent;
  }

  .remove-title {
    font-weight: 600;
    font-size: 0.75rem;
  }

  .remove-spinner {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    margin-left: 0.5em;
  }
}
</style>
